import type {
  MergeColumnChoices,
  MergeEligibility,
  MergeFailureReason,
  MergeIneligibleReason,
  MergeNameFrom,
  MergePreview,
  MergeRunStatus,
} from "./mergeItem.types";

/** What a disabled picker row says, per spec §8. */
export const INELIGIBLE_REASON_TEXT: Record<MergeIneligibleReason, string> = {
  not_found: "Item not found or you don't have access",
  subitem: "Subitems can't be merged",
  same_board: "Same board as this item",
  board_in_group: "This item is already synced to this board",
  other_synced: "Already synced. Unlink it first, or open it and merge from there.",
  already_merged: "Already synced with this item",
};

export const PAYWALL_MESSAGE =
  "Merging would take your account past its plan's limit of synced items. Neither item was changed.";

/** What the result screen says when nothing was merged. */
export const failureMessage = (reason?: MergeFailureReason): string => {
  if (!reason) return "Something went wrong. Neither item was changed.";
  if (reason === "paywall") return PAYWALL_MESSAGE;
  if (reason === "monday_error") return "monday didn't accept the change. Neither item was changed, so you can try again.";
  if (reason === "invalid_request") return "The request was incomplete. Close this and try again.";
  return `${INELIGIBLE_REASON_TEXT[reason]}. Neither item was changed.`;
};

/** SIMB's visual indicator, which sync setup puts in front of synced item names. */
const VISUAL_INDICATOR = "✨";

const comparableName = (name: string): string =>
  name.replace(VISUAL_INDICATOR, "").trim().replace(/\s+/g, " ").toLowerCase();

/** A hint only (spec §2): nothing is ever matched automatically. */
export const isSameName = (a: string, b: string): boolean => comparableName(a) === comparableName(b);

/** Same-name items first, then the order monday returned them in. */
export const sortCandidates = <T extends { name: string }>(items: T[], currentName: string): T[] => {
  const same = items.filter((item) => isSameName(item.name, currentName));
  const rest = items.filter((item) => !isSameName(item.name, currentName));
  return [...same, ...rest];
};

export const isEligible = (eligibility?: MergeEligibility): boolean => eligibility?.eligible === true;

/** Columns that differ and SIMB can write: the rows step 2 asks the user to choose on (M14). */
export const changingColumns = (preview: MergePreview) =>
  preview.shared.filter((column) => !column.same && column.writable);

/** Step 2's starting picks: each column's `defaultFrom`, as the preview computed it. */
export const defaultColumnChoices = (preview: MergePreview): MergeColumnChoices =>
  Object.fromEntries(changingColumns(preview).map((column) => [column.columnKey, column.defaultFrom ?? "current"]));

/** Every choosable column set to one side: "Use all". */
export const allColumnChoices = (preview: MergePreview, side: MergeNameFrom): MergeColumnChoices =>
  Object.fromEntries(changingColumns(preview).map((column) => [column.columnKey, side]));

/**
 * What the merge replaces, per side, given the picks (M15). Taking the other item's value replaces
 * it on this item AND on every board this item is already synced to; taking this item's value
 * replaces it on the other item only. The name counts as a column here.
 */
export const replacedBySide = (
  preview: MergePreview,
  nameFrom: MergeNameFrom,
  choices: MergeColumnChoices,
): { onOther: string[]; onCurrent: string[] } => {
  const onOther: string[] = [];
  const onCurrent: string[] = [];
  if (!isSameName(preview.current.name, preview.other.name)) {
    (nameFrom === "other" ? onCurrent : onOther).push("Name");
  }
  for (const column of changingColumns(preview)) {
    ((choices[column.columnKey] ?? "current") === "other" ? onCurrent : onOther).push(column.title);
  }
  return { onOther, onCurrent };
};

export const TERMINAL_STATES = new Set(["done", "partial", "failed", "unknown"]);

export type MergeOutcome = { status: MergeRunStatus; timedOut: false } | { status: null; timedOut: true };

export interface MergeOutcomeWaiter {
  promise: Promise<MergeOutcome>;
  /** A status from the socket (or anywhere else). Only a terminal one settles the wait. */
  settle: (status: MergeRunStatus) => void;
  /** Reads the status endpoint once: the fallback for a socket that may have missed the event. */
  recheck: () => void;
  cancel: () => void;
}

export interface MergeOutcomeTiming {
  /** No event by now: start checking the status endpoint. */
  overdueMs?: number;
  /** How often the status endpoint is read once overdue. */
  intervalMs?: number;
  /** Give up and show "Still working". */
  timeoutMs?: number;
}

/**
 * Waits for a merge's outcome. The socket event (`item_merge_completed`, relayed by
 * simb-notification-ms) is the primary signal; the status endpoint is the fallback, read on a
 * socket reconnect and on a slow timer once the result is overdue, because room emits are not
 * buffered. A failed status read is not the merge failing: the merge runs server-side either way.
 */
export const createMergeOutcomeWaiter = (
  fetchStatus: () => Promise<MergeRunStatus>,
  { overdueMs = 15_000, intervalMs = 10_000, timeoutMs = 120_000 }: MergeOutcomeTiming = {},
): MergeOutcomeWaiter => {
  let resolve!: (outcome: MergeOutcome) => void;
  const promise = new Promise<MergeOutcome>((r) => (resolve = r));
  let finished = false;
  const timers: Array<ReturnType<typeof setTimeout>> = [];

  const finish = (outcome: MergeOutcome) => {
    if (finished) return;
    finished = true;
    timers.forEach((timer) => clearTimeout(timer));
    clearInterval(interval);
    resolve(outcome);
  };

  const settle = (status: MergeRunStatus) => {
    if (TERMINAL_STATES.has(status.state)) finish({ status, timedOut: false });
  };

  const recheck = () => {
    if (finished) return;
    fetchStatus().then(settle, () => undefined);
  };

  let interval: ReturnType<typeof setInterval> | undefined;
  timers.push(
    setTimeout(() => {
      recheck();
      interval = setInterval(recheck, intervalMs);
    }, overdueMs),
    setTimeout(() => finish({ status: null, timedOut: true }), timeoutMs),
  );

  return {
    promise,
    settle,
    recheck,
    cancel: () => {
      finished = true;
      timers.forEach((timer) => clearTimeout(timer));
      clearInterval(interval);
    },
  };
};
